import { useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'

/**
 * lib/AnimatedNumber.tsx — the primitive for numbers that CHANGE.
 *
 * Numbers snapped everywhere in this product. A seat count going 3 → 4, a
 * price arriving on verification, a result count moving as filters change:
 * each of those is the answer to something the user just did, and each landed
 * with no acknowledgement at all.
 *
 * ── IT LEANS ON `.tabular`, WHICH ALREADY SOLVED THE HARD PART ────────────
 * The reason count-ups usually look cheap is glyph jitter — proportional
 * figures change width every frame and the whole row twitches. `.tabular`
 * (`font-variant-numeric: tabular-nums`) has been mandatory on every price and
 * carat since the design system shipped and NOTHING consumed it for motion.
 * This component applies it unconditionally, so the only thing that can still
 * shift is a change in DIGIT COUNT ($9.99 → $14.97) — which is what `reserve`
 * is for: pass the widest string the slot will ever hold and the box is sized
 * for it up front, so the layout never moves.
 *
 * ── DEFAULTS ARE DELIBERATE ───────────────────────────────────────────────
 * `animateOnMount` is OFF. A dashboard that counts up from zero every time you
 * navigate to it is the most overused trick of the last decade; the motion is
 * meaningful only when it encodes a CHANGE the user caused. First paint is
 * therefore instant and truthful.
 *
 * ── REDUCED MOTION ───────────────────────────────────────────────────────
 * `useReducedMotion()` short-circuits the tween entirely (no rAF loop is even
 * started) and the value snaps. The direction cue is CSS and has its own
 * `prefers-reduced-motion` block next to its `@keyframes` in `index.css`.
 *
 * ── SCREEN READERS ───────────────────────────────────────────────────────
 * The tween is plain text in a non-live region, so nothing is announced
 * mid-flight; AT reads the settled value whenever it reaches the node. Do NOT
 * wrap this in `aria-live="polite"` without passing `duration={0}` — you would
 * queue up ~25 announcements per change.
 */

/** Ease-out cubic — the house "feeling of arrival" curve. */
function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

export interface AnimatedNumberOptions {
  /** Tween length in ms. 0 (or reduced motion) = snap. Default 420. */
  duration?: number
  /** Count up from 0 on first paint. Default false — see the note above. */
  animateOnMount?: boolean
}

/**
 * The tween, without any rendering — returns the value to display right now.
 * Exported so a consumer can drive something other than text (a gauge, a
 * seat rail, a dial) off the same motion.
 */
export function useAnimatedNumber(value: number, opts: AnimatedNumberOptions = {}): number {
  const { duration = 420, animateOnMount = false } = opts
  const reduce = useReducedMotion()
  const target = Number.isFinite(value) ? value : 0
  const [display, setDisplay] = useState(() => (animateOnMount ? 0 : target))
  const mounted = useRef(false)
  // Read inside the rAF loop without re-subscribing the effect every frame.
  const displayRef = useRef(display)
  displayRef.current = display

  useEffect(() => {
    const first = !mounted.current
    mounted.current = true
    if (first && !animateOnMount) { setDisplay(target); return }

    const from = displayRef.current
    if (reduce || duration <= 0 || from === target) { setDisplay(target); return }

    let raf = 0
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      setDisplay(from + (target - from) * easeOut(t))
      if (t < 1) raf = requestAnimationFrame(tick)
      // Land EXACTLY on the target — floating-point easing lands at
      // 3.9999999 often enough that a rounded seat count would read "3".
      else setDisplay(target)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
    // `displayRef` is deliberately not a dep: retargeting mid-tween must start
    // from wherever the number currently is, read once at effect time.
  }, [target, duration, reduce, animateOnMount])

  return display
}

export interface AnimatedNumberProps extends AnimatedNumberOptions {
  value: number
  /**
   * How to render the tweened value. THIS is where rounding belongs — a seat
   * count must never render 3.4. Default rounds to an integer.
   */
  format?: (n: number) => string
  /**
   * The widest string this slot will ever hold (e.g. "$14.97"). Reserves the
   * box so a digit-count change cannot shift the layout. Optional.
   */
  reserve?: string
  className?: string
  /** `data-testid` for the settled text. */
  testId?: string
}

const defaultFormat = (n: number) => String(Math.round(n))

export function AnimatedNumber({
  value,
  format = defaultFormat,
  reserve,
  className = '',
  testId,
  ...opts
}: AnimatedNumberProps) {
  const reduce = useReducedMotion()
  const display = useAnimatedNumber(value, opts)
  const text = format(display)

  // Direction cue — the number rolls the way it moved. Purely decorative and
  // keyed off the SETTLED value so a retarget mid-tween doesn't flip it.
  const prev = useRef(value)
  const [dir, setDir] = useState<'up' | 'down' | null>(null)
  useEffect(() => {
    if (value === prev.current) return
    const next = value > prev.current ? 'up' : 'down'
    prev.current = value
    if (reduce) return
    setDir(next)
    const id = window.setTimeout(() => setDir(null), 420)
    return () => window.clearTimeout(id)
  }, [value, reduce])

  const body = (
    <span className={dir ? `num-roll num-roll--${dir}` : undefined} data-testid={testId}>
      {text}
    </span>
  )

  if (!reserve) {
    return <span className={`tabular ${className}`.trim()}>{body}</span>
  }
  return (
    <span className={`tabular num-slot ${className}`.trim()}>
      <span className="num-slot__ghost" aria-hidden="true">{reserve}</span>
      {body}
    </span>
  )
}
